import { useMemo, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { SplitButton } from 'primereact/splitbutton';
import { useSearchParams } from 'react-router-dom';

import { useExportCryoBabyLabelsMutation } from '@/app/services/aspirations';
import { useFeatureFlag } from '@/app/services/features';
import { AspirationStats, StopGoIcon, StopGoIconStatus } from '@/components';
import { useTempQcInfo } from '@/services/aspiration/hooks';
import {
  useAspirationFormReport,
  useOocyteCollectionFormReport,
} from '@/services/reports';
import {
  REFRESH_STOP_GO_SIGN,
  SHOW_STOP_SIGN,
  tog_chutesideqc,
} from '@/types.gql';
import { formatDateWithPattern } from '@/utils';

import { useAspirationContext } from '../hooks/useAspirationContext';
import { QC_REQUIRED_FIELDS as QC_REQUIRED_FIELDS_CHUTESIDE } from '../utils/constants';
import { AspirationQcInformationDialog } from './AspirationQcInformationDialog';

export const AspirationActions = ({
  className = '',
}: {
  className?: string;
}) => {
  const { setQcDataSubmitted } = useAspirationContext();
  const [params] = useSearchParams();
  const opuDate =
    params.get('d') || formatDateWithPattern(new Date(), 'MM-dd-yyyy');
  const opuSiteId = params.get('sId');
  const opuTechnicianId = params.get('otId');
  const chuteNumber = params.get('chuteNumber');
  const location = params.get('l') as string;

  const [showAspirationStats, setShowAspirationStats] = useState(false);
  const [showQcInformation, setShowQcInformation] = useState(false);

  const [cryoBabyLabels] = useExportCryoBabyLabelsMutation();
  const aspirationFormEnabled = useFeatureFlag('Print_Aspiration_Form');
  const oocyteCollectionFormFromScheduleEnabled = useFeatureFlag(
    'Oocyte_Collection_Form_From_Schedule',
  );
  const [generateAspirationForm] = useAspirationFormReport();
  const [generateOocyteCollectionForm] = useOocyteCollectionFormReport();
  const [refreshStopSign] = useLazyQuery(REFRESH_STOP_GO_SIGN);
  const { aspirations, aspSite } = useAspirationContext();

  const { data: showStopSignData, refetch: refetchShowStopSign } = useQuery(
    SHOW_STOP_SIGN,
    {
      variables: { siteId: opuSiteId as string },
      fetchPolicy: 'network-only',
      skip: !opuSiteId,
    },
  );

  const { data: tempQcData, refetch: refetchTempQcData } = useTempQcInfo({
    siteId: opuSiteId,
    aspirationDate: opuDate,
    location,
    chuteNumber: chuteNumber === '2' ? 100000001 : 100000000,
  });

  const handleMatingLabels = async () => {
    const labels = aspirations?.map((a) => ({
      accountCode: a.account,
      donorName: a.donor,
      donorRegNum: a.donorRegNum,
      export: a.export,
      nonEuComment: a.nonEuComment,
    }));

    const result = await cryoBabyLabels({
      labels,
      aspDate: opuDate,
      aspSite: aspSite?.name,
      location,
    });

    const excelBytes = result.data?.exportCryoBabyLabelXlsx;
    if (!excelBytes) return;
    const blob = new Blob([new Uint8Array(excelBytes)], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const excelUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = excelUrl;
    link.download = 'Cryo Baby Labels For Shipping - Aspiration.xlsx';
    link.click();
    setTimeout(() => URL.revokeObjectURL(excelUrl), 100);
  };

  useMemo(() => {
    if (!tempQcData) return;
    const allFilled = QC_REQUIRED_FIELDS_CHUTESIDE.every(
      (field) => tempQcData[field as keyof typeof tempQcData] ?? '',
    );
    setQcDataSubmitted(allFilled);
  }, [tempQcData, setQcDataSubmitted]);

  const disabled = !opuDate || !opuSiteId || !opuTechnicianId;
  const aspirationIds =
    aspirations
      ?.map((a) => a.aspirationId)
      .filter((id): id is string => !!id) ?? [];
  const printLabelsMenuItems = [
    ...(aspirationFormEnabled
      ? [
          {
            label: 'Print Aspiration Form',
            disabled: !aspirationIds.length,
            command: () => generateAspirationForm({ aspirationIds }),
          },
        ]
      : []),
    ...(oocyteCollectionFormFromScheduleEnabled
      ? [
          {
            label: 'Print Oocyte Collection Form',
            disabled: !aspirationIds.length && (!opuDate || !opuSiteId),
            command: () =>
              generateOocyteCollectionForm({
                aspirationIds,
                opuDate,
                opuSiteId: opuSiteId as string,
                location,
              }),
          },
        ]
      : []),
  ];

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`.trim()}>
      {printLabelsMenuItems.length ? (
        <SplitButton
          label="Print Labels"
          severity="secondary"
          disabled={disabled}
          onClick={handleMatingLabels}
          model={printLabelsMenuItems}
        />
      ) : (
        <Button
          label="Print Labels"
          severity="secondary"
          disabled={disabled}
          onClick={handleMatingLabels}
        />
      )}
      <Button
        label="QC Information"
        icon="pi pi-eye"
        severity="secondary"
        disabled={disabled}
        onClick={() => setShowQcInformation(true)}
      />
      <Button
        label="Aspiration Stats"
        icon="pi pi-eye"
        severity="secondary"
        disabled={disabled}
        onClick={() => setShowAspirationStats(true)}
      />

      <div
        className="relative z-10 flex h-10 w-10 cursor-pointer items-center justify-center transition-transform duration-500 [transform-style:preserve-3d] perspective-[1000px] hover:[transform:rotateY(180deg)]"
        onClick={async () => {
          await refreshStopSign({
            variables: {
              val: (!!showStopSignData?.showStopSign).toString(),
              siteId: opuSiteId as string,
            },
          });
          refetchShowStopSign();
        }}
      >
        <StopGoIcon
          status={
            showStopSignData?.showStopSign
              ? StopGoIconStatus.STOP
              : StopGoIconStatus.GO
          }
        />
      </div>

      <Dialog
        header="QC Information"
        visible={showQcInformation}
        onHide={() => setShowQcInformation(false)}
        className="w-full max-w-4xl"
      >
        <AspirationQcInformationDialog
          setShowQcInformation={setShowQcInformation}
          qcData={tempQcData as tog_chutesideqc}
          refetchQcData={refetchTempQcData}
        />
      </Dialog>

      <Dialog
        header="Aspiration Stats"
        visible={showAspirationStats}
        onHide={() => setShowAspirationStats(false)}
        footer={
          <Button
            label="Close"
            severity="secondary"
            onClick={() => setShowAspirationStats(false)}
          />
        }
        className="w-full max-w-4xl"
      >
        <AspirationStats
          aspirationDate={opuDate}
          siteId={opuSiteId as string}
        />
      </Dialog>
    </div>
  );
};
